/**
 * The icon kit: every icon in the game is a handful of flat shapes in a 32 x 32 box, drawn
 * like a sticker (flat colors, a dark outline around everything). The same data draws into
 * the page (as SVG) and onto canvas textures (signs, the TV, scratch-offs), so an icon looks
 * the same everywhere and never depends on the player's emoji font.
 */

/** One shape. Filled shapes get the ink outline unless they say otherwise. */
export interface Shape {
  /** SVG path data, in a 32 x 32 box. */
  d: string;
  /** Fill color. Leave it off for a line (stroke only). */
  fill?: string;
  /** Outline color; `false` for none. Defaults to the ink. */
  stroke?: string | false;
  /** Outline (or line) width. */
  w?: number;
  opacity?: number;
  /** Line ends; round unless it says otherwise. */
  cap?: 'round' | 'butt' | 'square';
}

export type IconDef = readonly Shape[];

/** The outline color: a warm near-black, like a felt pen. */
export const INK = '#2b1a10';
/** Default outline width for filled shapes, and for lines. */
export const OUTLINE = 1.6;
export const LINE = 1.8;

/** The shared palette. Prefer these so the set hangs together. */
export const P = {
  ink: INK,
  white: '#fffdf6',
  cream: '#f6ecd4',
  paper: '#f3ead8',
  beige: '#e6d2a8',
  tan: '#d6ac74',
  wood: '#b67a45',
  brown: '#8a5a33',
  darkBrown: '#5a3820',
  black: '#2c2c31',
  charcoal: '#4a4a52',
  darkGray: '#6b7178',
  gray: '#9aa1a8',
  lightGray: '#cfd5da',
  silver: '#dde3e8',
  steel: '#8b98a3',
  red: '#e1473b',
  darkRed: '#a92b22',
  rust: '#b4562a',
  orange: '#f48a2a',
  amber: '#f0a830',
  gold: '#f5c542',
  yellow: '#ffd43b',
  lemon: '#fff0a0',
  lime: '#a4e05a',
  green: '#4cae4f',
  darkGreen: '#2e7a3a',
  olive: '#7c8a3a',
  camo: '#677a3c',
  teal: '#2bb3a3',
  sky: '#8fd3ff',
  blue: '#3a80e0',
  denim: '#4b6fa8',
  navy: '#24406e',
  purple: '#8d5bd6',
  lavender: '#c6aaf2',
  pink: '#ff9cc2',
  hotPink: '#ff4fa3',
  skin: '#f3c39c',
  skinShade: '#d99a6c',
  /** The classic round yellow face. */
  face: '#ffd04d',
  blush: '#ff8f7d',
  meat: '#c9553e',
  bun: '#eab06a',
  mustard: '#f2c200',
  ketchup: '#d63a2b',
  foam: '#fffaf0',
  water: '#7cc6f2',
  glass: '#cfeaf7',
  fire: '#ff7a1a',
  flame: '#ffc23a',
  mud: '#7a5a34',
  grass: '#6fbf4a',
} as const;

const n = (v: number): number => Math.round(v * 100) / 100;

/** A circle. */
export const circle = (cx: number, cy: number, r: number): string =>
  `M${n(cx - r)} ${n(cy)}a${n(r)} ${n(r)} 0 1 0 ${n(2 * r)} 0a${n(r)} ${n(r)} 0 1 0 ${n(-2 * r)} 0Z`;

/** An ellipse (axis-aligned). */
export const ellipse = (cx: number, cy: number, rx: number, ry: number): string =>
  `M${n(cx - rx)} ${n(cy)}a${n(rx)} ${n(ry)} 0 1 0 ${n(2 * rx)} 0a${n(rx)} ${n(ry)} 0 1 0 ${n(-2 * rx)} 0Z`;

/** A rectangle with optionally rounded corners. */
export function rect(x: number, y: number, w: number, h: number, r = 0): string {
  if (r <= 0) return `M${n(x)} ${n(y)}h${n(w)}v${n(h)}h${n(-w)}Z`;
  const k = Math.min(r, w / 2, h / 2);
  return (
    `M${n(x + k)} ${n(y)}h${n(w - 2 * k)}a${n(k)} ${n(k)} 0 0 1 ${n(k)} ${n(k)}` +
    `v${n(h - 2 * k)}a${n(k)} ${n(k)} 0 0 1 ${n(-k)} ${n(k)}` +
    `h${n(-(w - 2 * k))}a${n(k)} ${n(k)} 0 0 1 ${n(-k)} ${n(-k)}` +
    `v${n(-(h - 2 * k))}a${n(k)} ${n(k)} 0 0 1 ${n(k)} ${n(-k)}Z`
  );
}

/** A closed polygon through the points (x0, y0, x1, y1, ...). */
export function poly(...pts: number[]): string {
  let d = `M${n(pts[0] ?? 0)} ${n(pts[1] ?? 0)}`;
  for (let i = 2; i + 1 < pts.length; i += 2) d += `L${n(pts[i] as number)} ${n(pts[i + 1] as number)}`;
  return `${d}Z`;
}

/** An open line through the points (x0, y0, x1, y1, ...). */
export function polyline(...pts: number[]): string {
  let d = `M${n(pts[0] ?? 0)} ${n(pts[1] ?? 0)}`;
  for (let i = 2; i + 1 < pts.length; i += 2) d += `L${n(pts[i] as number)} ${n(pts[i + 1] as number)}`;
  return d;
}

/** A filled shape with the ink outline. */
export const fill = (color: string, d: string, o: Omit<Shape, 'd' | 'fill'> = {}): Shape => ({
  d,
  fill: color,
  ...o,
});

/** A filled shape with no outline (stripes, labels, shading, details). */
export const flat = (color: string, d: string, opacity?: number): Shape => ({
  d,
  fill: color,
  stroke: false,
  ...(opacity === undefined ? {} : { opacity }),
});

/** A line, ink by default. */
export const line = (d: string, w = LINE, color: string = INK, opacity?: number): Shape => ({
  d,
  stroke: color,
  w,
  ...(opacity === undefined ? {} : { opacity }),
});

/** A white highlight. */
export const shine = (d: string, opacity = 0.5): Shape => ({ d, fill: '#ffffff', stroke: false, opacity });

/** Stands in for an icon that hasn't been drawn yet (a test makes sure none ship). */
export const TODO: IconDef = [
  { d: rect(4, 4, 24, 24, 6), fill: '#d5d9dc', stroke: '#9aa1a8', w: 1.6 },
  { d: 'M11 11L21 21M21 11L11 21', stroke: '#9aa1a8', w: 2.4 },
];
